package com.schoolconnect.app.feature.support

import com.schoolconnect.app.repository.support.SupportTicketsDashboard
import com.schoolconnect.app.repository.support.SupportTicketsRepository
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.feature.common.PaginationPolicy
import io.ktor.http.ContentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlin.time.Duration.Companion.days

data class SupportTicketUi(
    val id: String,
    val backendId: String,
    val subject: String,
    val raisedOn: String,
    val updatedOn: String,
    val status: String
)

data class SupportTicketDetailUi(
    val id: String,
    val subject: String,
    val description: String,
    val raisedOn: String,
    val updatedOn: String,
    val status: String,
    val conversation: List<SupportTicketMessageUi>,
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList()
)

data class SupportTicketMessageUi(
    val sender: String,
    val message: String,
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList()
)

data class SupportTicketStatisticsUi(
    val total: Int = 0,
    val open: Int = 0,
    val inProgress: Int = 0,
    val resolved: Int = 0,
    val closed: Int = 0
)

data class SupportTicketsUiState(
    val query: String = "",
    val status: String = "All Statuses",
    val periodRange: String = "All Time",
    val periodFrom: String = "",
    val periodTo: String = "",
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val sortBy: String = "updated_at",
    val sortAscending: Boolean = false,
    val tickets: List<SupportTicketUi> = emptyList(),
    val openCount: Int = 0,
    val inProgressCount: Int = 0,
    val resolvedCount: Int = 0,
    val statistics: SupportTicketStatisticsUi = SupportTicketStatisticsUi(),
    val isStatisticsLoading: Boolean = true,
    val statisticsErrorMessage: String = "",
    val isLoading: Boolean = true,
    val errorMessage: String = "",
    val isSaving: Boolean = false,
    val saveErrorMessage: String = "",
    val saveGeneration: Int = 0,
    val selectedTicket: SupportTicketDetailUi? = null,
    val isDetailLoading: Boolean = false,
    val detailErrorMessage: String = "",
    val isReplying: Boolean = false,
    val replyErrorMessage: String = "",
    val replyGeneration: Int = 0
)

data class SupportTicketAttachment(
    val name: String,
    val bytes: ByteArray,
    val contentType: ContentType
)

class SupportTicketsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var saveJob: Job? = null
    private var searchJob: Job? = null
    private var detailJob: Job? = null
    private var replyJob: Job? = null
    private var statisticsJob: Job? = null
    private val _uiState = MutableStateFlow(SupportTicketsUiState())
    val uiState: StateFlow<SupportTicketsUiState> = _uiState.asStateFlow()

    init {
        load()
        loadStatistics()
    }

    fun retry() {
        load(forceRemote = true)
        loadStatistics()
    }

    fun searchChanged(value: String) {
        _uiState.value = _uiState.value.copy(query = value, pageIndex = 0)
        searchJob?.cancel()
        searchJob = scope.launch {
            delay(300)
            load(forceRemote = true)
        }
    }
    fun statusChanged(value: String) {
        searchJob?.cancel()
        _uiState.value = _uiState.value.copy(status = value, pageIndex = 0)
        load(forceRemote = true)
    }
    fun periodChanged(value: String) {
        searchJob?.cancel()
        _uiState.value = _uiState.value.copy(periodRange = value, periodFrom = "", periodTo = "", pageIndex = 0)
        load(forceRemote = true)
    }
    fun customPeriodChanged(from: String, to: String) {
        searchJob?.cancel()
        _uiState.value = _uiState.value.copy(periodRange = "Custom Range", periodFrom = from, periodTo = to, pageIndex = 0)
        load(forceRemote = true)
    }
    fun filtersCleared() {
        searchJob?.cancel()
        _uiState.value = _uiState.value.copy(
            query = "",
            status = "All Statuses",
            periodRange = "All Time",
            periodFrom = "",
            periodTo = "",
            pageIndex = 0
        )
        load(forceRemote = true)
    }
    fun pageChanged(value: Int) { _uiState.value = _uiState.value.copy(pageIndex = value.coerceAtLeast(0)); load() }
    fun pageSizeChanged(value: Int) {
        val size = value.takeIf { it in PaginationPolicy.PAGE_SIZE_OPTIONS } ?: PaginationPolicy.DEFAULT_PAGE_SIZE
        _uiState.value = _uiState.value.copy(pageSize = size, pageIndex = 0)
        load()
    }

    fun sortChanged(sortBy: String, ascending: Boolean) {
        _uiState.value = _uiState.value.copy(sortBy = sortBy, sortAscending = ascending, pageIndex = 0)
        load(sortBy = sortBy, order = if (ascending) "asc" else "desc")
    }

    fun openTicket(ticketId: String, preserveCurrentDetail: Boolean = false) {
        detailJob?.cancel()
        _uiState.value = _uiState.value.copy(
            isDetailLoading = true,
            detailErrorMessage = "",
            selectedTicket = if (preserveCurrentDetail) _uiState.value.selectedTicket else null
        )
        detailJob = scope.launch {
            SupportTicketsRepository.getTicketDetails(ticketId)
                .onSuccess { detail -> _uiState.value = _uiState.value.copy(selectedTicket = detail.toUi(), isDetailLoading = false) }
                .onFailure { error -> _uiState.value = _uiState.value.copy(isDetailLoading = false, detailErrorMessage = error.message.orEmpty().ifBlank { "Unable to load ticket details." }) }
        }
    }

    fun replyToTicket(ticketId: String, message: String, attachment: SupportTicketAttachment?) {
        replyJob?.cancel()
        _uiState.value = _uiState.value.copy(isReplying = true, replyErrorMessage = "")
        replyJob = scope.launch {
            SupportTicketsRepository.replyToTicket(
                ticketId = ticketId,
                message = message,
                attachmentName = attachment?.name,
                attachmentBytes = attachment?.bytes,
                attachmentContentType = attachment?.contentType
            ).onSuccess { detail ->
                val responseDetail = detail.toUi()
                val currentDetail = _uiState.value.selectedTicket
                val displayedDetail = if (responseDetail.conversation.isNotEmpty() || currentDetail == null) {
                    responseDetail
                } else {
                    currentDetail.copy(
                        subject = responseDetail.subject,
                        updatedOn = responseDetail.updatedOn,
                        status = responseDetail.status,
                        conversation = currentDetail.conversation + SupportTicketMessageUi(sender = "You", message = message)
                    )
                }
                _uiState.value = _uiState.value.copy(
                    isReplying = false,
                    selectedTicket = displayedDetail,
                    detailErrorMessage = "",
                    replyGeneration = _uiState.value.replyGeneration + 1
                )
                openTicket(ticketId, preserveCurrentDetail = true)
                load(forceRemote = true)
                loadStatistics()
            }.onFailure { error -> _uiState.value = _uiState.value.copy(isReplying = false, replyErrorMessage = error.message.orEmpty().ifBlank { "Unable to send the reply." }) }
        }
    }

    fun createTicket(subject: String, description: String, attachment: SupportTicketAttachment?) {
        saveJob?.cancel()
        _uiState.value = _uiState.value.copy(isSaving = true, saveErrorMessage = "")
        saveJob = scope.launch {
            // Compose JS can finish the button click-sound interaction after the
            // callback starts. Keep the dialog/button attached while the loading
            // state is visible before beginning the network mutation.
            delay(500)
            // Repository/client validation is allowed to fail before a Result is
            // created. Keep that failure inside the route-scoped job so invalid
            // form input cannot surface as an uncaught web runtime error.
            val result = runCatching {
                SupportTicketsRepository.createTicket(
                    subject = subject,
                    description = description,
                    attachmentName = attachment?.name,
                    attachmentBytes = attachment?.bytes,
                    attachmentContentType = attachment?.contentType
                ).getOrThrow()
            }
            result.onSuccess {
                _uiState.value = _uiState.value.copy(isSaving = false, saveErrorMessage = "", saveGeneration = _uiState.value.saveGeneration + 1)
                load()
                loadStatistics()
            }.onFailure { error ->
                _uiState.value = _uiState.value.copy(isSaving = false, saveErrorMessage = error.message.orEmpty().ifBlank { "Unable to submit the support ticket." })
            }
        }
    }

    private fun loadStatistics() {
        statisticsJob?.cancel()
        _uiState.value = _uiState.value.copy(isStatisticsLoading = true, statisticsErrorMessage = "")
        statisticsJob = scope.launch {
            SupportTicketsRepository.loadStatistics()
                .onSuccess { response ->
                    val statistics = SupportTicketStatisticsUi(
                        total = response.total ?: 0,
                        open = response.open ?: 0,
                        inProgress = response.inProgress ?: 0,
                        resolved = response.resolved ?: 0,
                        closed = response.closed ?: 0
                    )
                    _uiState.value = _uiState.value.copy(
                        statistics = statistics,
                        openCount = statistics.open,
                        inProgressCount = statistics.inProgress,
                        resolvedCount = statistics.resolved,
                        isStatisticsLoading = false,
                        statisticsErrorMessage = ""
                    )
                }
                .onFailure { error ->
                    _uiState.value = _uiState.value.copy(
                        isStatisticsLoading = false,
                        statisticsErrorMessage = error.message.orEmpty().ifBlank { "Unable to load support ticket statistics." }
                    )
                }
        }
    }

    private fun load(sortBy: String = _uiState.value.sortBy, order: String = if (_uiState.value.sortAscending) "asc" else "desc", forceRemote: Boolean = false) {
        loadJob?.cancel()
        val requested = _uiState.value
        _uiState.value = requested.copy(isLoading = true, errorMessage = "")
        loadJob = scope.launch {
            SupportTicketsRepository.loadPage(
                query = requested.query,
                status = requested.status,
                periodStart = requested.periodStartApi(),
                periodEnd = requested.periodEndApi(),
                pageIndex = requested.pageIndex,
                pageSize = requested.pageSize,
                sortBy = sortBy,
                order = order,
                forceRemote = forceRemote
            )
                // Merge into the latest state so a list response cannot restore
                // stale statistics-loading flags after the analytics request
                // has already completed.
                .onSuccess { items -> _uiState.value = items.toUiState(_uiState.value) }
                .onFailure { error ->
                    _uiState.value = _uiState.value.copy(
                        isLoading = false,
                        errorMessage = error.message.orEmpty().ifBlank { "Unable to load support tickets." }
                    )
                }
        }
    }

    fun clear() { loadJob?.cancel(); saveJob?.cancel(); searchJob?.cancel(); detailJob?.cancel(); replyJob?.cancel(); statisticsJob?.cancel(); scope.cancel() }
}

private fun SupportTicketsUiState.periodStartApi(): String? = when (periodRange) {
    "Last 30 Days" -> kotlin.time.Clock.System.now().minus(30.days).toString()
    "Last 90 Days" -> kotlin.time.Clock.System.now().minus(90.days).toString()
    "Custom Range" -> appDateInputToApi(periodFrom)?.let { "${it}T00:00:00" }
    else -> null
}

private fun SupportTicketsUiState.periodEndApi(): String? = when (periodRange) {
    "Custom Range" -> appDateInputToApi(periodTo)?.let { "${it}T23:59:59.999999" }
    else -> null
}

private fun List<com.schoolconnect.app.datasource.network.response.TicketListItemResponse>.toUiState(previous: SupportTicketsUiState) = SupportTicketsUiState(
    query = previous.query,
    status = previous.status,
    periodRange = previous.periodRange,
    periodFrom = previous.periodFrom,
    periodTo = previous.periodTo,
    pageIndex = previous.pageIndex,
    pageSize = previous.pageSize,
    totalCount = serverTotalCountOrNull() ?: size,
    sortBy = previous.sortBy,
    sortAscending = previous.sortAscending,
    tickets = map { ticket ->
        SupportTicketUi(
            id = ticket.ticketId.orEmpty().takeUnless { value -> value.looksLikeUuid() }.orEmpty().ifBlank { ticket.id.orEmpty() },
            backendId = listOf(ticket.id, ticket.ticketId).firstOrNull { value -> value.looksLikeUuid() }
                ?: ticket.id.orEmpty().ifBlank { ticket.ticketId.orEmpty() },
            subject = ticket.subject.orEmpty().ifBlank { "Untitled ticket" },
            raisedOn = ticket.createdAt.orEmpty(),
            updatedOn = ticket.updatedAt.orEmpty(),
            status = ticket.status.orEmpty()
        )
    },
    openCount = previous.openCount,
    inProgressCount = previous.inProgressCount,
    resolvedCount = previous.resolvedCount,
    statistics = previous.statistics,
    isStatisticsLoading = previous.isStatisticsLoading,
    statisticsErrorMessage = previous.statisticsErrorMessage,
    isLoading = false,
    // Preserve the mutation generation while the post-save refresh replaces
    // the list state. The screen consumes this edge to close the dialog.
    saveGeneration = previous.saveGeneration
)

private fun String?.looksLikeUuid(): Boolean = this.orEmpty().matches(
    Regex("^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$")
)

private fun com.schoolconnect.app.datasource.network.response.TicketResponse.toUi() = SupportTicketDetailUi(
    id = ticketId.orEmpty().ifBlank { id.orEmpty() },
    subject = subject.orEmpty().ifBlank { "Untitled ticket" },
    description = description.orEmpty(),
    raisedOn = createdAt.orEmpty(),
    updatedOn = updatedAt.orEmpty(),
    status = status.orEmpty(),
    conversation = buildList {
        messages.orEmpty().forEach { message ->
            add(
                SupportTicketMessageUi(
                    sender = message.senderName.orEmpty().ifBlank { message.senderType.orEmpty().ifBlank { "Support" } },
                    message = message.message.orEmpty(),
                    attachmentFileIds = message.attachmentFileIds.orEmpty(),
                    attachmentFileUrls = message.attachmentFileUrls.orEmpty()
                )
            )
        }
    },
    attachmentFileIds = attachmentFileIds.orEmpty(),
    attachmentFileUrls = attachmentFileUrls.orEmpty()
)
